// MIDAS V3 — New Chat dialog (Phase 4)
//
// Centered modal that opens from the rail's "+ New chat" button (both
// expanded and collapsed variants). Lets the user start either:
//   • Platform-mode chat (managers / admins only — gated on `user.role`)
//   • Machine-context chat (filtered by user scope)
//
// Source: ToDo/MidasChat/Claude_Design_files/machineid/
//         design_handoff_midas_v3/chat-window.jsx — `NewChatDialog` (lines 361-499)
//
// Data sourcing (NOT the handoff's `MACHINES` fixture):
//   • Machines come from `customerMachinery.machines` (same source as v2's
//     MidasFleetChipsV2). Worker scoping is enforced by the backend — the
//     slice already only contains machines the current user is entitled to
//     see. This mirrors v2 exactly.
//   • Status pip color is derived from `activeServiceRequestCount` per the
//     v2 diode logic — open SR ⇒ "stopped" (red), healthy ⇒ "running" (green),
//     unknown ⇒ "idle" (muted amber).
//   • R3 sub-task 3 (Path A): user-facing wording is "Running" / "Stopped" /
//     "Idle" (live-PLC vocabulary). The internal V3Status keys stay verbatim
//     ("running" / "idle" / "stopped"); only the i18n fallback copy + the
//     mapping helper are unified. The mapping helper now lives in
//     ./v3-utils/machineStatus.ts so Path B (MIDAS-V3-LIVE-STATUS-BACKEND)
//     has a single swap point: when live PLC telemetry lands, only the
//     mapping function body changes and every call site here keeps reading
//     the same vocabulary.
//
// Dispatch pattern (mirrors v2's PlatformModeCardV2 + MidasSidebarV2):
//   Platform pick → startNewChat() + setActiveThreadId(null) + setMachineContext(null)
//   Machine pick  → startNewChat() + setActiveThreadId(null) + setNewThreadPending()
//                   + setMachineContextDirect({ machineId, model, ... })
//                   + (debounced) chatBotApi.setMachineContext(machineId)
//
// No `kind` field added to startNewChat — v2 already differentiates Platform
// vs Machine via the subsequent setMachineContext dispatch. Mirroring that
// exact pattern keeps chatBotSlice.ts untouched and avoids any v2 regression
// surface. (See spec §5 — backwards-compatible alternative.)
//
// Permission gating:
//   isManager = user?.role !== Roles.WORKER (same as the rest of the app —
//   see customer/operations/**/canManage and customer/useCustomerRoutes.tsx).
//   Workers do not see the Platform card. Backend already rejects worker
//   platform-mode requests (SEC-REQ-1, verified in v2).
//
// Keyboard:
//   • Esc → close
//   • ↑ / ↓ → move focus among rows (Platform option + visible machine rows)
//   • Enter → activate focused row
//   • Tab / Shift+Tab → cycle within the dialog (manual focus trap)
//
// No MUI, no @emotion, no `sx`, no `dt` tokens. The modal is a plain <div>
// portal'd to document.body via ReactDOM.createPortal so it always renders
// above the rest of the app regardless of stacking context.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Roles } from "../../../../account/models/AccountModels";
import {
  setMachineContext,
  setMachineContextDirect,
  setNewThreadPending,
  startNewChat,
} from "../../../../shared/chatBot/chatBotSlice";
import chatBotApi from "../../../../shared/chatBot/ChatBotApi";
import { getMachines } from "../../../machinery/customerMachinerySlice";
import { Machine } from "../../../machinery/models/models";
import { setActiveThreadId } from "../../midasPageSlice";
// R3 sub-task 3 (Path A): shared SR-count → status helper. Single swap point
// for Path B (live PLC) — see ./v3-utils/machineStatus.ts.
import { mapSrCountToStatus } from "./v3-utils/machineStatus";

// ============================================================
// Props
// ============================================================

interface NewChatDialogV3Props {
  open: boolean;
  onClose: () => void;
  /** Element to restore focus to when the dialog closes. */
  triggerRef?: React.RefObject<HTMLElement>;
}

// ============================================================
// Status pip helper — port of MidasFleetChipsV2.getDiodeState into
// the v3 "running / idle / stopped" vocabulary.
// ============================================================

type V3Status = "running" | "idle" | "stopped";

// R3 sub-task 3 (Path A): delegate the SR-count → label mapping to the shared
// helper. `fleetLoading` short-circuits to "idle" before calling the helper
// because, while the fleet is still being fetched, we don't yet have a
// trustworthy `activeServiceRequestCount` to read. Once Path B (live PLC)
// lands, the helper's body changes and this call site keeps the same shape.
const machineStatusOf = (m: Machine, fleetLoading: boolean): V3Status => {
  if (fleetLoading) return "idle";
  const visual = mapSrCountToStatus(m.activeServiceRequestCount);
  switch (visual.label) {
    case "Running":
      return "running";
    case "Stopped":
      return "stopped";
    case "Idle":
    default:
      return "idle";
  }
};

const statusPipBg = (s: V3Status): string => {
  switch (s) {
    case "running":
      return "var(--midas-v3-green)";
    case "stopped":
      return "var(--midas-v3-red)";
    case "idle":
    default:
      return "var(--midas-v3-amber)";
  }
};

const statusPillStyles = (
  s: V3Status
): { background: string; color: string } => {
  switch (s) {
    case "running":
      return {
        background: "color-mix(in oklab, var(--midas-v3-green) 16%, var(--midas-v3-card))",
        color: "var(--midas-v3-green)",
      };
    case "stopped":
      return {
        background: "color-mix(in oklab, var(--midas-v3-red) 16%, var(--midas-v3-card))",
        color: "var(--midas-v3-red)",
      };
    case "idle":
    default:
      return {
        background: "color-mix(in oklab, var(--midas-v3-amber) 16%, var(--midas-v3-card))",
        color: "var(--midas-v3-amber)",
      };
  }
};

// ============================================================
// Flatten the grouped machines map (same shape v2 uses).
// ============================================================

const flattenMachines = (
  machinesMap: Record<string, Machine[] | undefined> | null | undefined
): Machine[] => {
  if (!machinesMap || typeof machinesMap !== "object") return [];
  const flat: Machine[] = [];
  Object.values(machinesMap).forEach((g) => {
    if (Array.isArray(g)) flat.push(...(g as Machine[]));
  });
  return flat;
};

// Build the location line per Round 5 spec (matches MidasFleetChipsV2).
const formatLocation = (m: Machine): string => {
  const primary = m.locationName?.trim() || "";
  const secondary = m.parentLocationName?.trim() || "";
  if (primary && secondary) return `${primary} · ${secondary}`;
  if (primary) return primary;
  if (secondary) return secondary;
  return "—";
};

// ============================================================
// NewChatDialogV3
// ============================================================

const NewChatDialogV3: React.FC<NewChatDialogV3Props> = ({
  open,
  onClose,
  triggerRef,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("chatBot");
  const t = (k: string, f: string) =>
    translate(k as any, { defaultValue: f }) as string;

  // Manager gating — `user?.role !== Roles.WORKER` mirrors the pattern used
  // across customer/operations/** and Header.tsx. Admin/Manager see Platform;
  // Worker doesn't. Viewer never reaches /midas-v3 (ViewerRouteGuard).
  const user = useAppSelector((s) => s.account.user);
  const isManager = !!user?.role && user.role !== Roles.WORKER;

  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const machineryLoading = useAppSelector(
    (s) => s.customerMachinery.loading
  );

  const allMachines = useMemo(
    () => flattenMachines(machinesMap),
    [machinesMap]
  );

  // Lazy-fetch machines if the slice is empty on open. Mirrors v2.
  useEffect(() => {
    if (open && allMachines.length === 0 && !machineryLoading) {
      dispatch(getMachines({}));
    }
  }, [open, allMachines.length, machineryLoading, dispatch]);

  // ── local UI state ─────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setFocusedIndex(0);
    }
  }, [open]);

  // ── filtered list ─────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allMachines;
    return allMachines.filter((m) => {
      const model = (m.model || "").toLowerCase();
      const sn = (m.serialNumber || "").toLowerCase();
      const brand = (
        m.customBrandName ||
        m.manufacturer?.name ||
        ""
      ).toLowerCase();
      const typeName = (m.type?.name || "").toLowerCase();
      const loc = (
        (m.locationName || "") +
        " " +
        (m.parentLocationName || "")
      ).toLowerCase();
      return (
        model.includes(q) ||
        sn.includes(q) ||
        brand.includes(q) ||
        typeName.includes(q) ||
        loc.includes(q)
      );
    });
  }, [allMachines, query]);

  // ── keyboard nav model ─────────────────────────────────────
  // Index 0 = Platform card (when visible). Machines start at 1 (or 0 if no
  // Platform card). Capped to visible range.
  const platformOffset = isManager ? 1 : 0;
  const totalRows = platformOffset + filtered.length;

  // Clamp focusedIndex when list size changes.
  useEffect(() => {
    if (focusedIndex >= totalRows) {
      setFocusedIndex(Math.max(0, totalRows - 1));
    }
  }, [totalRows, focusedIndex]);

  // ── refs ───────────────────────────────────────────────────
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const platformBtnRef = useRef<HTMLButtonElement | null>(null);
  const machineBtnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Cache the trigger element on open so we can restore focus on close.
  const cachedTriggerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      cachedTriggerRef.current =
        triggerRef?.current ||
        (document.activeElement as HTMLElement | null) ||
        null;
      // Move focus into the dialog: search input is the most useful entry.
      // Defer to next tick so the portal node exists.
      const id = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
    // On close, restore focus to the trigger.
    const el = cachedTriggerRef.current;
    if (el && typeof el.focus === "function") {
      el.focus();
    }
    cachedTriggerRef.current = null;
    return undefined;
  }, [open, triggerRef]);

  // ── dispatch helpers ─────────────────────────────────────
  const pickPlatform = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    // Tell the bot (best-effort) to drop machine mode AND clear local context.
    dispatch(setMachineContext(null));
    onClose();
    navigate("/midas-v3");
  }, [dispatch, navigate, onClose]);

  // Debounced bot-side setMachineContext. Mirrors v2's MidasSidebarV2 logic
  // so the bot exits/enters machine mode without spamming the API.
  const contextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (contextTimerRef.current) {
        clearTimeout(contextTimerRef.current);
      }
    },
    []
  );

  const pickMachine = useCallback(
    (machine: Machine) => {
      dispatch(startNewChat());
      dispatch(setActiveThreadId(null));
      dispatch(setNewThreadPending());
      dispatch(
        setMachineContextDirect({
          machineId: machine.id,
          model: machine.model,
          serialNumber: machine.serialNumber,
          internalNumber: machine.internalNumber,
          system: machine.system,
          manufacturerName:
            machine.customBrandName || machine.manufacturer?.name || "",
          typeName: machine.type?.name,
          axisConfigName: machine.axisConfiguration?.name,
          controlSystemBrandName: machine.controlSystemBrand?.name,
          imageContent: machine.image?.content,
          description: machine.description,
          productionYear: machine.productionYear,
        })
      );
      if (contextTimerRef.current) {
        clearTimeout(contextTimerRef.current);
      }
      contextTimerRef.current = setTimeout(() => {
        chatBotApi.setMachineContext(machine.id).catch(() => {
          /* Best-effort — bot may be offline */
        });
      }, 300);
      onClose();
      navigate("/midas-v3");
    },
    [dispatch, navigate, onClose]
  );

  // Activate whatever the keyboard cursor is sitting on.
  const activateFocused = useCallback(() => {
    if (totalRows === 0) return;
    if (isManager && focusedIndex === 0) {
      pickPlatform();
      return;
    }
    const m = filtered[focusedIndex - platformOffset];
    if (m) pickMachine(m);
  }, [
    totalRows,
    isManager,
    focusedIndex,
    filtered,
    platformOffset,
    pickPlatform,
    pickMachine,
  ]);

  // ── global key handler ─────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex((i) => Math.min(totalRows - 1, i + 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === "Enter") {
        // Only intercept Enter when the focus is in the search input — the
        // visual cursor is what should activate. If focus is on a real
        // button, default Enter behaviour (click that button) still works.
        if (document.activeElement === searchInputRef.current) {
          e.preventDefault();
          activateFocused();
        }
        return;
      }
      // Manual focus trap on Tab / Shift+Tab — cycle within the dialog.
      if (e.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const enabled = Array.from(focusables).filter(
          (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1
        );
        if (enabled.length === 0) return;
        const first = enabled[0];
        const last = enabled[enabled.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, totalRows, onClose, activateFocused]);

  // ── i18n labels ─────────────────────────────────────────
  const labelTitle = t("midas.v3.newChatDialog.title", "New chat");
  const labelEyebrow = t(
    "midas.v3.newChatDialog.eyebrow",
    "Choose context"
  );
  const labelCloseAria = t("midas.v3.newChatDialog.closeAria", "Close dialog");
  const labelPlatformTitle = t(
    "midas.v3.newChatDialog.platform.title",
    "PLATFORM · MACHINE ID"
  );
  const labelPlatformDesc = t(
    "midas.v3.newChatDialog.platform.description",
    "Chat about the platform itself — add machines, manage users, ask about reports across the fleet."
  );
  const labelPlatformBadge = t(
    "midas.v3.newChatDialog.platform.badge",
    "MANAGERS"
  );
  const labelDivider = t(
    "midas.v3.newChatDialog.divider",
    "Or pick a machine"
  );
  const labelSearchPlaceholder = t(
    "midas.v3.newChatDialog.search.placeholder",
    "Search by name, S/N, or location…"
  );
  const labelFooterNavigate = t(
    "midas.v3.newChatDialog.footer.navigate",
    "↑↓ navigate"
  );
  const labelFooterSelect = t(
    "midas.v3.newChatDialog.footer.select",
    "↵ select"
  );
  const labelFooterClose = t(
    "midas.v3.newChatDialog.footer.close",
    "esc close"
  );
  const labelManagerAccess = t(
    "midas.v3.newChatDialog.footer.managerAccess",
    "Manager access"
  );
  const labelOperatorAccess = t(
    "midas.v3.newChatDialog.footer.operatorAccess",
    "Operator access"
  );
  // R3 sub-task 3 (Path A): status pill copy unified to the live-PLC vocabulary
  // ("Running" / "Stopped" / "Idle") even though the underlying signal is still
  // SR-count (Path A). When Path B (MIDAS-V3-LIVE-STATUS-BACKEND) lands, machines
  // with edge agents start showing real telemetry — same labels, same pip colors,
  // no visual retraining for users. The mapping helper in ./v3-utils/machineStatus.ts
  // is the single swap point.
  //   • Pip colors (green / red / amber) stay — they're correct under either
  //     semantics.
  const labelStatusRunning = t(
    "midas.v3.newChatDialog.machineStatus.running",
    "Running"
  );
  const labelStatusIdle = t(
    "midas.v3.newChatDialog.machineStatus.idle",
    "Idle"
  );
  const labelStatusStopped = t(
    "midas.v3.newChatDialog.machineStatus.stopped",
    "Stopped"
  );
  const statusLabel = (s: V3Status): string => {
    switch (s) {
      case "running":
        return labelStatusRunning;
      case "stopped":
        return labelStatusStopped;
      case "idle":
      default:
        return labelStatusIdle;
    }
  };
  const labelEmptyState = t(
    "midas.v3.newChatDialog.emptyState",
    "No machines match your search."
  );
  const labelNoMachines = t(
    "midas.v3.newChatDialog.noMachines",
    "No machines yet."
  );

  if (!open) return null;

  // ── portal target — append to body so we sit above the v3 root and
  // any rail z-index stacks.
  const portalTarget = typeof document !== "undefined" ? document.body : null;
  if (!portalTarget) return null;

  // Wrap the modal in `.midas-v3-root.p-...` so token vars resolve. We
  // mirror the current theme onto the portal so the dialog matches the page.
  const theme =
    document.querySelector(".midas-v3-root.p-claude") !== null
      ? "p-claude"
      : "p-machineid";

  const dialog = (
    <div
      className={`midas-v3-root ${theme}`}
      // Sit ABOVE the v3 layout. Pointer-events default to auto.
      //
      // R2 sub-task 4: `background: transparent` override is REQUIRED.
      // The `.midas-v3-root` class is applied here purely so the v3 token
      // custom properties resolve inside the portalled dialog (see the
      // comment block above at lines 499-504). But that class ALSO sets
      // `background: var(--midas-v3-bg)` (opaque cream) on its primary
      // selector in midas-v3-tokens.scss line 29-42. Without the explicit
      // transparent override here, the portal wrapper paints an opaque cream
      // fill over the entire viewport and the rgba backdrop below ends up
      // dimming the cream paint, not the actual page DOM behind it.
      // Net effect: dialog opens, page goes solid cream. The override
      // defeats that paint while keeping token resolution intact.
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "transparent",
      }}
      role="presentation"
      onMouseDown={(e) => {
        // Backdrop close — only when the click starts on the backdrop itself,
        // not inside the dialog content.
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15, 23, 30, 0.32)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
        }}
      />

      {/* Centered modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="midas-v3-new-chat-title"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(560px, calc(100vw - 32px))",
          maxHeight: "min(640px, calc(100vh - 32px))",
          background: "var(--midas-v3-card)",
          border: "1px solid var(--midas-v3-border)",
          borderRadius: 14,
          boxShadow:
            "0 20px 60px rgba(15, 23, 30, 0.35), 0 4px 12px rgba(15, 23, 30, 0.12)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          color: "var(--midas-v3-ink)",
          fontFamily: "var(--font-sans)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--midas-v3-border-soft)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            id="midas-v3-new-chat-title"
            style={{
              fontWeight: 600,
              fontSize: 15,
              color: "var(--midas-v3-ink)",
            }}
          >
            {labelTitle}
          </div>
          <span className="midas-v3-eyebrow">{labelEyebrow}</span>
          <span style={{ flex: 1 }} />
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label={labelCloseAria}
            title={labelCloseAria}
            style={{
              width: 28,
              height: 28,
              padding: 0,
              borderRadius: 6,
              background: "transparent",
              border: "1px solid transparent",
              color: "var(--midas-v3-muted)",
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
              fontFamily: "inherit",
              fontSize: 14,
              lineHeight: 1,
              transition: "background-color .12s, color .12s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--midas-v3-card-2)";
              e.currentTarget.style.color = "var(--midas-v3-ink)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--midas-v3-muted)";
            }}
          >
            ✕
          </button>
        </div>

        {/* Platform option (managers only) */}
        {isManager && (
          <button
            ref={platformBtnRef}
            type="button"
            onClick={pickPlatform}
            onMouseEnter={() => setFocusedIndex(0)}
            aria-label={`${labelPlatformTitle} — ${labelPlatformDesc}`}
            data-focused={focusedIndex === 0 ? "true" : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 18px",
              margin: "12px 14px 0",
              background: "var(--midas-v3-accent-soft)",
              border:
                focusedIndex === 0
                  ? "1px solid var(--midas-v3-accent)"
                  : "1px solid var(--midas-v3-accent-line)",
              borderRadius: 10,
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "inherit",
              color: "var(--midas-v3-ink)",
              outline: "none",
              transition: "border-color .12s, background-color .12s",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: "var(--midas-v3-accent)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--font-mono)",
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              ◆
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="mono"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--midas-v3-accent)",
                  letterSpacing: "0.08em",
                }}
              >
                {labelPlatformTitle}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--midas-v3-ink-2)",
                  marginTop: 2,
                  whiteSpace: "normal",
                }}
              >
                {labelPlatformDesc}
              </div>
            </div>
            <span
              className="mono"
              style={{
                fontSize: 9,
                padding: "2px 7px",
                borderRadius: 999,
                background: "var(--midas-v3-accent)",
                color: "#fff",
                letterSpacing: "0.08em",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {labelPlatformBadge}
            </span>
          </button>
        )}

        {/* Divider row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "16px 18px 6px",
          }}
        >
          <span className="midas-v3-eyebrow">{labelDivider}</span>
          <span
            aria-hidden="true"
            style={{
              flex: 1,
              height: 1,
              background: "var(--midas-v3-border-soft)",
            }}
          />
        </div>

        {/* Search input */}
        <div style={{ padding: "0 14px 10px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              border: "1px solid var(--midas-v3-border)",
              background: "var(--midas-v3-bg-soft)",
              borderRadius: 8,
            }}
          >
            <span
              aria-hidden="true"
              style={{ color: "var(--midas-v3-faint)" }}
            >
              ⌕
            </span>
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                // Reset cursor when the list re-shapes.
                setFocusedIndex(isManager ? 0 : 0);
              }}
              placeholder={labelSearchPlaceholder}
              aria-label={labelSearchPlaceholder}
              style={{
                flex: 1,
                border: 0,
                outline: 0,
                background: "transparent",
                fontFamily: "inherit",
                fontSize: 13,
                color: "var(--midas-v3-ink)",
                minWidth: 0,
              }}
            />
            <span
              className="mono"
              style={{
                fontSize: 10.5,
                color: "var(--midas-v3-faint)",
                flexShrink: 0,
              }}
            >
              {filtered.length}/{allMachines.length}
            </span>
          </div>
        </div>

        {/* Machine list */}
        <div
          role="listbox"
          aria-label={labelDivider}
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "0 8px 12px",
            minHeight: 80,
          }}
        >
          {allMachines.length === 0 ? (
            <div
              style={{
                padding: "24px 18px",
                textAlign: "center",
                fontSize: 13,
                color: "var(--midas-v3-muted)",
              }}
            >
              {labelNoMachines}
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                padding: "24px 18px",
                textAlign: "center",
                fontSize: 13,
                color: "var(--midas-v3-muted)",
              }}
            >
              {labelEmptyState}
            </div>
          ) : (
            filtered.map((m, i) => {
              const rowIndex = i + platformOffset;
              const isFocused = rowIndex === focusedIndex;
              const status = machineStatusOf(m, machineryLoading);
              const pillStyles = statusPillStyles(status);
              const brand =
                m.customBrandName || m.manufacturer?.name || "";
              const displayName = brand
                ? `${brand} ${m.model || ""}`.trim()
                : m.model || "—";
              const typeName = m.type?.name || "";
              const sn = m.serialNumber || "";
              return (
                <button
                  key={m.id}
                  ref={(el) => {
                    machineBtnRefs.current[i] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={isFocused}
                  onClick={() => pickMachine(m)}
                  onMouseEnter={(e) => {
                    setFocusedIndex(rowIndex);
                    e.currentTarget.style.background =
                      "var(--midas-v3-card-2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isFocused
                      ? "var(--midas-v3-card-2)"
                      : "transparent";
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 10px",
                    width: "100%",
                    background: isFocused
                      ? "var(--midas-v3-card-2)"
                      : "transparent",
                    border: 0,
                    borderRadius: 8,
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "inherit",
                    color: "var(--midas-v3-ink)",
                    outline: "none",
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: statusPipBg(status),
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--midas-v3-ink)",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {displayName}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "var(--midas-v3-muted)",
                        display: "flex",
                        gap: 10,
                        marginTop: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {typeName && <span>{typeName}</span>}
                      {sn && (
                        <span
                          className="mono"
                          style={{ color: "var(--midas-v3-faint)" }}
                        >
                          SN {sn}
                        </span>
                      )}
                      <span
                        aria-hidden="true"
                        style={{ color: "var(--midas-v3-faint)" }}
                      >
                        ·
                      </span>
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {formatLocation(m)}
                      </span>
                    </div>
                  </div>
                  <span
                    className="mono"
                    style={{
                      fontSize: 9.5,
                      padding: "2px 6px",
                      borderRadius: 4,
                      background: pillStyles.background,
                      color: pillStyles.color,
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      flexShrink: 0,
                    }}
                  >
                    {statusLabel(status)}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div
          className="mono"
          style={{
            padding: "10px 18px",
            borderTop: "1px solid var(--midas-v3-border-soft)",
            background: "var(--midas-v3-card-2)",
            fontSize: 10.5,
            color: "var(--midas-v3-faint)",
            letterSpacing: "0.04em",
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span>
            {labelFooterNavigate} · {labelFooterSelect} · {labelFooterClose}
          </span>
          <span>{isManager ? labelManagerAccess : labelOperatorAccess}</span>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, portalTarget);
};

export default NewChatDialogV3;
